<template>
  <div
    :style="sizeStyle"
    :class="[classes.wrapper, classes[layoutClass], 'background']"
    ref="wrapper"
  >
    <transition mode="out-in" name="fade">
      <span
        v-show="displayOverlay"
        :class="[classes.overlay, 'overlay']"
        @click="displayOverlay = !displayOverlay"
      />
    </transition>

    <slot />
  </div>
</template>

<script lang="ts">
import {
  useDimensionSync,
  syncLayoutDependencies
} from "@fresh8/vue-utils/src/dimensionSync";
import { defineComponent, computed, ref, watchEffect } from "vue";
import { createUseStyles } from "@fresh8/vue-jss/src";
import {
  injectBrand,
  injectAdTag
} from "@fresh8/ad-data-composition/src/brand";
import { injectRuntimeConfig } from "@fresh8/ad-data-composition/src/runtimeConfig";
import { provideDisplayOverlay } from "../lib/provideInjectors";
import { provideLayout, useLayout, STRAPLINE } from "../hooks/useLayout";
import { provideAdSize } from "../hooks/useAdSize";

const useStyles = createUseStyles({
  wrapper: {
    backgroundColor: props => props.backgroundColor || "",
    borderRadius: props => props.borderRadius || 0,
    padding: "5px 8px",
    boxSizing: "border-box",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    position: "relative"
  },
  overlay: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: 900,
    background: "rgba(0,0,0,0.6)"
  },
  [STRAPLINE]: {
    padding: "2px 5px 1px"
  }
});

export default defineComponent({
  setup() {
    const displayOverlay = ref(false);
    const wrapper = ref(null);
    const rc = injectRuntimeConfig();
    const { adTemplateBranding } = injectBrand();
    const { adTagSize, responsive } = injectAdTag();
    const { dimensions } = useDimensionSync(wrapper);
    const layoutContext = useLayout(dimensions);

    provideLayout(layoutContext);
    provideAdSize(dimensions);

    watchEffect(onInvalidate => {
      let updateParentToken;
      function updateParent() {
        window.parent.postMessage(
          { type: "f8_size", contentHeight: dimensions.value.height },
          "*"
        );

        updateParentToken = setTimeout(updateParent, 100);
      }

      rc.value.globalEmit?.("adResize", {
        height: dimensions.value.height,
        width: dimensions.value.width
      });

      updateParent();
      onInvalidate(() => updateParentToken && clearTimeout(updateParentToken));
    });

    const size = computed(() => {
      rc.value.globalEmit?.("adResize", {
        height: dimensions.value.height,
        width: dimensions.value.width
      });

      const height = adTagSize.value?.height && `${adTagSize.value?.height}px`;

      if (responsive.value) {
        return {
          width: "100%",
          height: height,
          minHeight: !height && "270px"
        };
      }

      return {
        width: `${adTagSize.value?.width}px`,
        height: height
      };
    });

    syncLayoutDependencies([
      computed(() => size.value?.width),
      computed(() => size.value?.height),
      responsive
    ]);

    const reativeStyles = computed(() => ({
      ...size.value,
      ...adTemplateBranding.value
    }));

    const classesRef = useStyles(reativeStyles);
    provideDisplayOverlay(displayOverlay);

    return {
      classes: classesRef,
      wrapper,
      sizeStyle: size,
      displayOverlay,
      layoutClass: computed(() => layoutContext.layout.value)
    };
  }
});
</script>
